import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Divider,
  Chip,
  Tooltip,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  DeleteSweep as ClearAllIcon,
  History as HistoryIcon,
  Launch as LaunchIcon,
} from "@material-ui/icons";
import { formatTimestamp } from "services/notificationHistoryService";
import useAppState from "helpers/useAppState";
import useAppDispatch from "helpers/useAppDispatch";

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 400,
    maxWidth: "100vw",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  headerButton: {
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  },
  content: {
    flex: 1,
    overflowY: "auto",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
    opacity: 0.3,
  },
  historyItem: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.5, 2),
    position: "relative",
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  itemHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: theme.spacing(0.5),
  },
  itemContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    flex: 1,
  },
  iconSuccess: {
    color: theme.palette.success.main,
    fontSize: 20,
    marginTop: 2,
  },
  iconError: {
    color: theme.palette.error.main,
    fontSize: 20,
    marginTop: 2,
  },
  iconWarning: {
    color: theme.palette.warning.main,
    fontSize: 20,
    marginTop: 2,
  },
  iconInfo: {
    color: theme.palette.info.main,
    fontSize: 20,
    marginTop: 2,
  },
  messageText: {
    flex: 1,
    fontSize: "0.875rem",
    lineHeight: 1.4,
    wordBreak: "break-word",
  },
  itemFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(3.5),
  },
  timestampText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  urlText: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  deleteButton: {
    padding: 4,
    marginLeft: theme.spacing(1),
    opacity: 0.5,
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 1,
      backgroundColor: theme.palette.action.hover,
    },
  },
  chip: {
    height: 20,
    fontSize: "0.7rem",
    marginLeft: theme.spacing(1),
  },
  footer: {
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
}));

const getTypeIcon = (type, classes) => {
  switch (type) {
    case "success":
      return <SuccessIcon className={classes.iconSuccess} />;
    case "error":
      return <ErrorIcon className={classes.iconError} />;
    case "warning":
      return <WarningIcon className={classes.iconWarning} />;
    case "info":
    default:
      return <InfoIcon className={classes.iconInfo} />;
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case "success":
      return "Succès";
    case "error":
      return "Erreur";
    case "warning":
      return "Attention";
    case "info":
    default:
      return "Info";
  }
};

const HistoryItem = ({ entry, onDelete, onNavigate }) => {
  const classes = useStyles();
  const { relative, full } = useMemo(() => formatTimestamp(entry.timestamp), [
    entry.timestamp,
  ]);

  const handleClick = () => {
    if (entry.url) {
      onNavigate(entry.url);
    }
  };

  return (
    <>
      <ListItem className={classes.historyItem} onClick={handleClick} button>
        <Box className={classes.itemHeader}>
          <Box className={classes.itemContent}>
            {getTypeIcon(entry.type, classes)}
            <Typography className={classes.messageText}>
              {entry.message}
            </Typography>
          </Box>
          <Tooltip title="Supprimer">
            <IconButton
              className={classes.deleteButton}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(entry.id);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box className={classes.itemFooter}>
          <Box display="flex" alignItems="center">
            <Tooltip title={full}>
              <Typography className={classes.timestampText}>
                {relative}
              </Typography>
            </Tooltip>
            <Chip
              label={getTypeLabel(entry.type)}
              size="small"
              className={classes.chip}
              variant="outlined"
            />
          </Box>
          {entry.url && (
            <Tooltip title={`Aller à ${entry.url}`}>
              <Typography className={classes.urlText}>
                <LaunchIcon style={{ fontSize: 12 }} />
                {entry.url}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </ListItem>
      <Divider />
    </>
  );
};

HistoryItem.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["success", "error", "warning", "info"]).isRequired,
    timestamp: PropTypes.string.isRequired,
    url: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

const HistoryPanel = () => {
  const classes = useStyles();
  const routerHistory = useHistory();
  const { toastHistory } = useAppState();
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch({ type: "TOGGLE_HISTORY_PANEL", payload: false });
  };

  const handleDelete = (id) => {
    dispatch({ type: "DELETE_HISTORY_ENTRY", payload: id });
  };

  const handleClearAll = () => {
    dispatch({ type: "CLEAR_HISTORY" });
  };

  const handleNavigate = (url) => {
    handleClose();
    routerHistory.push(url);
  };

  const hasEntries = toastHistory?.items?.length > 0;

  return (
    <Drawer
      anchor="right"
      open={toastHistory?.isOpen || false}
      onClose={handleClose}
      classes={{ paper: classes.drawer }}
    >
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerTitle}>
          <HistoryIcon />
          <Typography variant="h6">Historique</Typography>
        </Box>
        <Box className={classes.headerActions}>
          {hasEntries && (
            <Tooltip title="Tout effacer">
              <IconButton
                className={classes.headerButton}
                size="small"
                onClick={handleClearAll}
              >
                <ClearAllIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            className={classes.headerButton}
            size="small"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        {hasEntries ? (
          <List disablePadding>
            {toastHistory.items.map((entry) => (
              <HistoryItem
                key={entry.id}
                entry={entry}
                onDelete={handleDelete}
                onNavigate={handleNavigate}
              />
            ))}
          </List>
        ) : (
          <Box className={classes.emptyState}>
            <HistoryIcon className={classes.emptyIcon} />
            <Typography variant="h6" gutterBottom>
              Aucun historique
            </Typography>
            <Typography variant="body2">
              L&apos;historique des actions apparaîtra ici.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {hasEntries && (
        <Box className={classes.footer}>
          <Typography
            variant="caption"
            color="textSecondary"
            align="center"
            display="block"
          >
            {toastHistory.items.length} entrée
            {toastHistory.items.length > 1 ? "s" : ""} dans l&apos;historique
          </Typography>
        </Box>
      )}
    </Drawer>
  );
};

export default HistoryPanel;
